/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from 'zod'

import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { DefaultValues, useForm } from 'react-hook-form'
import { FieldRenderer } from './FieldRenderer'
import { BaseFieldProps, InferFormValues, JsonFormSchemaV2 } from './types'
import { SheetTrigger } from '@/components/shadcn/sheet'
import { cn } from '@/lib/utils'
import { PinFilterMenu } from '../../AppPage2'
import { TPageFilter } from '../../AppPage2/types'
import { IconRefreshOutline, IconUnpinOutline } from '@/components/icons'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { TileFilter } from '../TileFilter'
import { ComponentTitle } from '../ComponentTitle'
import clsx from 'clsx'

const generateZodSchema = (schema: JsonFormSchemaV2) => {
  const zodSchemaMap: Record<string, any> = {}

  schema.properties.forEach((field) => {
    let fieldSchema

    switch (field.type) {
      case 'string':
        fieldSchema = z.string()
        if (field.format === 'date') {
          fieldSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
        } else if (field.format === 'date-time') {
          fieldSchema = z.string().datetime()
        } else if (field.format === 'file-url') {
          fieldSchema = z.instanceof(File)
        } else if (field.format === 'uri') {
          fieldSchema = z.string().url()
        } else {
          if (field.minLength) fieldSchema = fieldSchema.min(field.minLength)
          if (field.maxLength) fieldSchema = fieldSchema.max(field.maxLength)
          if (field.pattern) fieldSchema = fieldSchema.regex(new RegExp(field.pattern))
          if (field.choices) fieldSchema = z.enum(field.choices as [string, ...string[]])
        }
        break

      case 'integer':
      case 'number':
        fieldSchema = z.number()
        if (field.minimum !== undefined) fieldSchema = fieldSchema.min(field.minimum)
        if (field.maximum !== undefined) fieldSchema = fieldSchema.max(field.maximum)
        break

      case 'boolean':
        fieldSchema = z.boolean()
        break

      case 'array':
        if (field.items?.choices) {
          const optionType = field.items.choices[0]
          if (typeof optionType === 'string') {
            fieldSchema = z.array(z.enum(field.items.choices as [string, ...string[]]))
          } else {
            fieldSchema = z.array(
              z.enum(field.items.choices.map((option) => (option as any).value) as [string, ...string[]])
            )
          }
        } else {
          fieldSchema = z.array(z.string())
        }
        break

      case 'cascading':
        {
          // Create a schema for the cascading field
          const cascadingSchema: Record<string, any> = {}

          // Add level1 (always single select)
          cascadingSchema.level1 = z.string().optional()

          // Add level2 (always single select)
          cascadingSchema.level2 = z.string().optional()

          // Add level3 (can be single or multi select)
          if (field.isMultiSelect?.level3) {
            cascadingSchema.level3 = z.array(z.string()).optional()
          } else {
            cascadingSchema.level3 = z.string().optional()
          }

          // Add level4 if needed (can be single or multi select)
          if (field.isMultiSelect?.level4 !== undefined) {
            if (field.isMultiSelect.level4) {
              cascadingSchema.level4 = z.array(z.string()).optional()
            } else {
              cascadingSchema.level4 = z.string().optional()
            }
          }

          // Add level5 if needed (can be single or multi select)
          if (field.isMultiSelect?.level5 !== undefined) {
            if (field.isMultiSelect.level5) {
              cascadingSchema.level5 = z.array(z.string()).optional()
            } else {
              cascadingSchema.level5 = z.string().optional()
            }
          }

          fieldSchema = z.object(cascadingSchema)
        }
        break

      default:
        fieldSchema = z.any()
    }

    zodSchemaMap[field.id] = schema.required?.includes(field.id) ? fieldSchema : fieldSchema.optional()
  })

  return z.object(zodSchemaMap)
}

export const FormContentV2 = <TSchema extends JsonFormSchemaV2>({
  schema,
  variant = 'column',
  filterType,
  isConfiguringLayout = false,
  submitHandler,
  resetDefaults,
  closeSheetOnSubmit,
  className,
  pinFilter,
  unPinFilter,
  unPinnedFilters,
  pinnedFilters,
  tileFilterSchema,
  filterChangeHandler,
}: {
  schema: TSchema
  variant?: BaseFieldProps['variant']
  filterType?: 'pinned' | 'unPinned' | 'tile'
  isConfiguringLayout?: boolean
  submitHandler: (data: InferFormValues<TSchema>) => void
  resetDefaults?: InferFormValues<TSchema>
  closeSheetOnSubmit?: boolean
  className?: string
  pinFilter?: (selectedF: TPageFilter, side: 'left' | 'right') => void
  unPinFilter?: (selectedF: TPageFilter) => void
  unPinnedFilters?: TPageFilter[]
  pinnedFilters?: TPageFilter[]
  tileFilterSchema?: JsonFormSchemaV2
  filterChangeHandler?: (data: InferFormValues<JsonFormSchemaV2>) => void
}) => {
  const zodSchema = generateZodSchema(schema)
  type FormValues = InferFormValues<TSchema>

  // Create initial values with proper typing
  const createDefaultValues = (schema: TSchema): DefaultValues<FormValues> => {
    return schema.properties.reduce((acc, field) => {
      let defaultValue: any = field.default

      // if (resetDefaults) {
      //   defaultValue = resetDefaults[field.id as keyof typeof resetDefaults]
      // }

      // Handle specific types
      if (field.type === 'array' && !defaultValue) {
        defaultValue = []
      } else if (field.type === 'boolean' && defaultValue === undefined) {
        defaultValue = false
      } else if (
        (field.type === 'string' || field.type === 'number' || field.type === 'integer') &&
        defaultValue === undefined
      ) {
        defaultValue = ''
      } else if (field.type === 'cascading' && !defaultValue) {
        // Initialize empty cascading field structure
        defaultValue = {
          level1: '',
          level2: '',
          level3: field.isMultiSelect?.level3 ? [] : '',
        }
      }

      return {
        ...acc,
        [field.id]: defaultValue,
      }
    }, {} as DefaultValues<FormValues>)
  }

  const defaultValues = createDefaultValues(schema)

  const form = useForm<FormValues>({
    resolver: zodResolver(zodSchema),
    defaultValues,
  })

  const { reset } = form
  useEffect(() => {
    if (resetDefaults) {
      reset(resetDefaults)
    }
  }, [resetDefaults, reset])

  const submitButton = (
    <Button
      variant={filterType && variant === 'row' ? 'ghost' : 'default'}
      size={filterType && variant === 'row' ? 'icon' : 'sm'}
      className={clsx({ 'ml-1 size-6 text-xs hover:text-primary': variant === 'row' })}
      type="submit"
    >
      {filterType ? variant === 'row' ? <IconRefreshOutline size={16} /> : 'Apply' : 'Submit'}
    </Button>
  )

  const { properties } = schema
  const fieldsToRender = useMemo(() => {
    if (!filterType || filterType === 'tile') {
      return properties
    }
    if (filterType === 'pinned' && pinnedFilters) {
      return properties.filter((property) => pinnedFilters.map((f) => f.id).includes(property.id))
    }
    if (filterType === 'unPinned' && unPinnedFilters) {
      return properties.filter((property) => unPinnedFilters.map((f) => f.id).includes(property.id))
    }
    return []
  }, [filterType, pinnedFilters, properties, unPinnedFilters])

  return (
    <div className="flex h-full flex-col">
      {variant === 'column' && schema.title && (
        <div className="flex w-full flex-wrap items-center justify-between gap-4 border-b pb-4">
          <ComponentTitle title={schema.title} description={schema.description} />
          <TileFilter schema={tileFilterSchema} submitHandler={filterChangeHandler} />
        </div>
      )}

      <Form {...form}>
        <ScrollArea className="h-full">
          <form
            onSubmit={form.handleSubmit(submitHandler)}
            className={cn(
              {
                'w-full max-w-2xl space-y-8 pt-4': variant === 'column',
                'flex flex-row items-center gap-x-2': variant === 'row',
              },
              className
            )}
          >
            {/* {variant === 'column' && (
          <div className="space-y-2">
            <h2 className="text-xl font-bold">{schema.title}</h2>
            {schema.description && <p className="text-muted-foreground">{schema.description}</p>}
          </div>
        )} */}
            {fieldsToRender.map((property) => {
              if (unPinnedFilters && pinFilter) {
                const filter = unPinnedFilters.find((f) => f.id === property.id)
                return (
                  <div className="flex items-center">
                    {isConfiguringLayout && <PinFilterMenu filter={filter} pinFilter={pinFilter} />}
                    <FieldRenderer field={property} control={form.control} variant={variant} />
                  </div>
                )
              }
              if (pinnedFilters && unPinFilter) {
                const filter = pinnedFilters.find((f) => f.id === property.id)
                if (!filter) {
                  return null
                }
                return (
                  <div className="flex items-center">
                    {isConfiguringLayout && (
                      <Button variant="ghost" size="icon" className="size-5" onClick={() => unPinFilter(filter)}>
                        <IconUnpinOutline className="size-4" />
                      </Button>
                    )}
                    <FieldRenderer field={property} control={form.control} variant={variant} />
                  </div>
                )
              }
              return <FieldRenderer field={property} control={form.control} variant={variant} />
            })}
            {closeSheetOnSubmit && form.formState.isValid ? (
              <SheetTrigger asChild>{submitButton}</SheetTrigger>
            ) : (
              submitButton
            )}
          </form>
        </ScrollArea>
      </Form>
    </div>
  )
}
